import * as Yup from "utils/yup"
import { buildEmailYupSchema } from "utils/emailValidation"

export const TENANT_NAME_MAX_LENGTH = 100
export const TENANT_SLUG_MAX_LENGTH = 63
export const TENANT_DOMAIN_MAX_LENGTH = 253

const tenantNameLetterPattern = /[\u0621-\u064Aa-zA-Z]/
const tenantNamePattern = /^[\u0621-\u064Aa-zA-Z\s]+$/
const tenantSlugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/
const tenantDomainPattern =
  /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,63}$/

export const buildTenantNameSchema = t =>
  Yup.string()
    .required(t("Tenant name is required"))
    .min(2, t("Tenant name must be at least 2 characters"))
    .max(
      TENANT_NAME_MAX_LENGTH,
      t("Tenant name cannot exceed {{max}} characters", { max: TENANT_NAME_MAX_LENGTH })
    )
    .matches(
      tenantNamePattern,
      t("Tenant name can only contain letters and spaces")
    )
    .test("has-letter", t("Tenant name must contain at least one letter"), value =>
      tenantNameLetterPattern.test(value || "")
    )

export const buildTenantSlugSchema = t =>
  Yup.string()
    .required(t("Slug is required"))
    .min(2, t("Slug must be at least 2 characters"))
    .max(
      TENANT_SLUG_MAX_LENGTH,
      t("Slug cannot exceed {{max}} characters", { max: TENANT_SLUG_MAX_LENGTH })
    )
    .matches(
      tenantSlugPattern,
      t("Slug can only contain lowercase letters, numbers, and hyphens")
    )

export const buildTenantDomainSchema = t =>
  Yup.string()
    .required(t("Domain is required"))
    .max(
      TENANT_DOMAIN_MAX_LENGTH,
      t("Domain cannot exceed {{max}} characters", { max: TENANT_DOMAIN_MAX_LENGTH })
    )
    .matches(
      tenantDomainPattern,
      t("Enter a valid domain (e.g. acme.halfmillion.app)")
    )

export const buildTenantWebsiteDomainSchema = t =>
  Yup.string()
    .max(
      TENANT_DOMAIN_MAX_LENGTH,
      t("Domain cannot exceed {{max}} characters", { max: TENANT_DOMAIN_MAX_LENGTH })
    )
    .matches(
      tenantDomainPattern,
      t("Enter a valid website domain (e.g. shop.acme.com)")
    )

export const buildTenantAccountIdSchema = t =>
  Yup.string()
    .required(t("Account ID is required"))
    .transform(value => String(value || "").trim().toUpperCase())
    .min(4, t("Account ID must be at least 4 characters"))
    .max(32, t("Account ID cannot exceed 32 characters"))
    .matches(
      /^[A-Za-z0-9]+$/,
      t("Account ID can only contain letters and numbers")
    )

export const buildTenantOrderPrefixSchema = t =>
  Yup.string()
    .required(t("Order prefix is required"))
    .transform(value => String(value || "").trim().toUpperCase())
    .matches(
      /^[A-Z]{2,4}$/,
      t("Order prefix must be 2 to 4 uppercase letters (A-Z)")
    )

export const buildTenantFormSchema = (
  t,
  { isEdit = false, includeStatus = true, includeOrderNumbering = true } = {}
) =>
  Yup.object({
    name: buildTenantNameSchema(t),
    slug: buildTenantSlugSchema(t),
    ...(isEdit ? { accountId: buildTenantAccountIdSchema(t) } : {}),
    adminEmail: buildEmailYupSchema(),
    domain: buildTenantDomainSchema(t),
    hasWebsite: Yup.boolean(),
    websiteDomain: Yup.string().when("hasWebsite", {
      is: true,
      then: () =>
        buildTenantWebsiteDomainSchema(t).required(
          t("Website domain is required when customer website is enabled")
        ),
      otherwise: schema => schema.optional().nullable(),
    }),
    hasQrWebsite: Yup.boolean(),
    qrWebsiteDomain: Yup.string().when("hasQrWebsite", {
      is: true,
      then: () =>
        buildTenantWebsiteDomainSchema(t).required(
          t("QR website domain is required when QR website is enabled")
        ),
      otherwise: schema => schema.optional().nullable(),
    }),
    hasCustomerAndroidApp: Yup.boolean(),
    hasCustomerIosApp: Yup.boolean(),
    hasRiderAndroidApp: Yup.boolean(),
    hasRiderIosApp: Yup.boolean(),
    contactEmail: buildEmailYupSchema(),
    ...(includeOrderNumbering
      ? {
          orderPrefix: isEdit
            ? Yup.string()
                .transform(value => String(value || "").trim().toUpperCase())
                .test(
                  "order-prefix-format",
                  t("Order prefix must be 2 to 4 uppercase letters (A-Z)"),
                  value => !value || /^[A-Z]{2,4}$/.test(value)
                )
            : buildTenantOrderPrefixSchema(t),
          orderSerialPadding: Yup.number()
            .typeError(t("Serial padding must be a number"))
            .integer(t("Serial padding must be a whole number"))
            .min(5, t("Serial padding must be at least 5 digits"))
            .max(20, t("Serial padding cannot exceed 20 digits"))
            .default(13),
        }
      : {}),
    ...(includeStatus
      ? { status: Yup.string().required(t("Status is required")) }
      : {}),
  })

const isSlugTakenMessage = msg => {
  const normalized = String(msg || "").toLowerCase()
  return (
    normalized.includes("slug") ||
    normalized.includes("record already exists") ||
    normalized.includes("already taken")
  )
}

const isNameTakenMessage = msg => {
  const normalized = String(msg || "").toLowerCase()
  return normalized.includes("tenant with this name") || normalized.includes("name already")
}

const isDomainTakenMessage = msg => {
  const normalized = String(msg || "").toLowerCase()
  return (
    (normalized.includes("domain is already") ||
      normalized.includes("domain already")) &&
    !normalized.includes("website domain")
  )
}

const isWebsiteDomainTakenMessage = msg => {
  const normalized = String(msg || "").toLowerCase()
  return (
    normalized.includes("website domain") &&
    !normalized.includes("qr website domain")
  )
}

const isQrWebsiteDomainTakenMessage = msg => {
  const normalized = String(msg || "").toLowerCase()
  return normalized.includes("qr website domain")
}

const isContactEmailTakenMessage = msg => {
  const normalized = String(msg || "").toLowerCase()
  return normalized.includes("contact email")
}

const isAdminEmailTakenMessage = msg => {
  const normalized = String(msg || "").toLowerCase()
  return (
    normalized.includes("email already") ||
    normalized.includes("email already taken") ||
    (normalized.includes("email") &&
      !isContactEmailTakenMessage(msg))
  )
}

const isPrefixTakenMessage = msg => {
  const normalized = String(msg || "").toLowerCase()
  return normalized.includes("order prefix")
}

const isAccountIdTakenMessage = msg => {
  const normalized = String(msg || "").toLowerCase()
  return normalized.includes("account id")
}

export const mapTenantApiErrorToField = (response, t) => {
  const message =
    response?.msg ||
    response?.message ||
    response?.error ||
    t("Failed to save tenant. Please try again.")

  if (isNameTakenMessage(message)) {
    return { field: "name", message }
  }
  if (isDomainTakenMessage(message)) {
    return { field: "domain", message }
  }
  if (isWebsiteDomainTakenMessage(message)) {
    return { field: "websiteDomain", message }
  }
  if (isQrWebsiteDomainTakenMessage(message)) {
    return { field: "qrWebsiteDomain", message }
  }
  if (isContactEmailTakenMessage(message)) {
    return { field: "contactEmail", message }
  }
  if (isAdminEmailTakenMessage(message)) {
    return { field: "adminEmail", message }
  }
  if (isSlugTakenMessage(message)) {
    return { field: "slug", message }
  }
  if (isPrefixTakenMessage(message)) {
    return { field: "orderPrefix", message }
  }
  if (isAccountIdTakenMessage(message)) {
    return { field: "accountId", message }
  }
  return { field: null, message }
}

export const applyTenantApiError = (response, { formik, dispatch, showAlert, t }) => {
  const { field, message } = mapTenantApiErrorToField(response, t)
  const translatedMessage = t(message, { defaultValue: message })

  if (field) {
    formik.setFieldError(field, translatedMessage)
    formik.setFieldTouched(field, true, false)
  }

  dispatch(
    showAlert({
      type: "error",
      text: translatedMessage,
    })
  )
}

export const getTenantActionPayload = actionResult =>
  actionResult?.payload ?? actionResult

export const isTenantActionSuccess = actionResult =>
  Boolean(getTenantActionPayload(actionResult)?.status)
